import { defineStore } from "pinia";
import {billingService}  from "@/utils/billingRequest";
import {chargeService} from "@/utils/chargeRequest";
import { orderBaseURL } from "@/utils/orderRequest";
import { payoutBaseURL } from "@/utils/payOutRequest.js";
import { checkWhetherHasGutZoomer } from "@/utils/commonActions.js";

import gql from "graphql-tag";
import { pns_graphal_client } from "@/utils/graphQLRequests.js";

export const usePaymentStore = defineStore("PaymentStore", {
  state: () => {
    return {
      // ******************************** query params ********************************
      // --------------- order info and auth params ----------------
      accession_id: null,
      sample_id: null,
      token: null,
      // --------------- order info and auth params Ends----------------

      // --------------- flow type params ----------------
      payment_prioritized: null,
      amount_to_pay:null,
      direct_to_blooddraw: null, // normally this param should be always null in payment step
      // --------------- flow type params Ends ----------------
      // --------------- other params ----------------
      count: null,
      // --------------- other params Ends ----------------
      // ******************************** query params Ends ********************************

      patient_id: null,
      customer_id: null,
      order_id: null,
      order_source: null,
      provider_has_revenue: null,
      provider_has_merchandise: null,
      has_gut_zoomer: null,
      is_family_order: null,
      family_order_sample_ids: null,

      // whether the (legacy) concierge blood draw fee was already paid on this order.
      // The concierge service was sunset on 09/01/2026 and can no longer be purchased on PNS,
      // but orders that already paid keep the scheduling redirect to the phleb concierge page.
      is_concierge_paid:null,

      // setting info
      consultation_fee_name: 'Consultation Fee',
      customized_pns_logo:'',
      // pnsSetting.joinBetaProgram: string '0' (false) or '1' (true); reserved for gating beta features
      join_beta_program: '0',
      // provider clinic info
      clinic_name: '',
      provider_name: '',
      charge_method: "",
      address_needed:null,
      blood_draw_needed: null,
      non_blood_draw_needed: null,
      // billing info
      billingInfo: '',
      charge_type: null,
      payment_status_index:null,
      // issue info
      // whether the order has billing issue (default to be true)
      has_billing_issue:true,

      case_insensitive_domestic_name_collection:[
        'US',
        'UNITED STATES',
        'THE UNITED STATES',
        'UNITED STATE',
        'THE UNITED STATE',
        'AMERICA',
        'U.S.',
        'U.S'
      ],
      is_international_payment:false,

      supported_bnpl_methods_data:null,

      current_basic_and_shipping_information:null,

      sample_types_mapping_backend_to_url_params:{
        "Stool" : 'Stool',
        "Saliva(4 tubes)": 'Saliva (4 tubes)',
        "Saliva(1 tube)": 'Saliva (1 tube)',
        "Microtube(Blood)": 'Microtube (Blood)',
        "Urine(4 tubes)":'Urine (4 tubes)',
        "Urine(1 tube)": 'Urine',
        "DBS Card": 'DBS Card',
      },
      sample_types_params:'',
        
      loadingPromo: false,

      // VIB-1764: guards the Pay buttons against duplicate charge submissions
      is_submitting_payment: false,

      billing_email: "",
      billing_phone: "",
      checkEmptyFields: false,
      invalidCountryWarning: false,
      hasEmptyFields: [],
      billing_country: "",
      billing_state: "",
      billing_address: "",
      billing_address2: "",
      billing_city: "",
      billing_zipCode: "",
      billing_firstName: "",
      billing_lastName: "",
    };
  },
  actions: {
    setIsSubmittingPayment(val){
      this.is_submitting_payment = val;
    },
    async storeAllQueryInfomation(query){
      // Normally this funtion will be called and these data are assigned, when initiate PaymentMedia page before visiting PaymentForm page, 
      // But, when users refresh on PaymentForm page, this function will need to be triggered.
      if(query){
        console.log('has query params');
        this.accession_id = query.accession_id;
        this.sample_id = query.sample_id;
        this.token = query.token;

        this.count = query.count;

        this.direct_to_blooddraw = query.direct_to_blooddraw == 'true'; // Normally this params should be null in payment step
        this.amount_to_pay = query.amount_to_pay;
        this.payment_prioritized = query.payment_prioritized == 'true';
      }else{
        console.log('no query params');
        // if no query params: reject error
        throw new Error('No query params');
      }
    },
    async initiatePaymentMediaPage(){
      try{
        //get and assign all ids and order source for PaymentMedia page's initiation
        const all_ids_and_order_source_data = await this.getAllIdsAndOrderSourceViaSampleId(this.sample_id);
        this.order_id = all_ids_and_order_source_data?.orderIdStr;
        this.patient_id = all_ids_and_order_source_data?.patientId;
        this.customer_id = all_ids_and_order_source_data?.customerId;
        this.order_source = all_ids_and_order_source_data?.order_source;

        //get and assign charge_type
        const payment_media_page_data = await this.getPaymentMediaPageData(this.accession_id, this.sample_id, this.order_id, this.patient_id, this.customer_id);
        this.payment_status_index = payment_media_page_data?.transaction;

      }catch(err){
        console.log('initiatePaymentMediaPage Err',err);
        throw new Error(err?.message ?? "Failed to initiate Payment Media Page.");
      }
    },
    async initiatePaymentFormPage(){
      try{
        //get and assign all ids and order type related data for PaymentForm page's initiation.
        const all_ids_and_order_type_data = await this.getAllIdsAndOrderTypeDataViaSampleId(this.sample_id);
        this.order_id = all_ids_and_order_type_data?.orderIdStr;
        this.patient_id = all_ids_and_order_type_data?.patientId;
        this.customer_id = all_ids_and_order_type_data?.customerId;
        this.provider_has_revenue = all_ids_and_order_type_data?.providerHasRevenue;
        this.provider_has_merchandise = all_ids_and_order_type_data?.providerHasMerchandise;
        this.charge_method = all_ids_and_order_type_data?.chargeMethod;
        this.address_needed = all_ids_and_order_type_data?.bloodKitDeliveryMethod == 'shipToPatient' || all_ids_and_order_type_data?.nonBloodKitDeliveryMethod == 'shipToPatient';
        this.has_gut_zoomer = checkWhetherHasGutZoomer(all_ids_and_order_type_data?.orderItems);
        //assign is_family_order and family_order_sample_ids
        this.is_family_order = all_ids_and_order_type_data?.isFamilyOrder;
        this.family_order_sample_ids = all_ids_and_order_type_data?.familyOrderSampleIds;

        //initiate other PaymentForm required data
        const payment_form_page_data = await this.getPaymentFormPageData(this.accession_id, this.sample_id, this.order_id, this.patient_id, this.customer_id);
        this.customized_pns_logo = payment_form_page_data?.pnsSetting?.pns_brand_logo;
        this.consultation_fee_name = payment_form_page_data?.pnsSetting?.consultation_fee_name;
        this.join_beta_program = payment_form_page_data?.pnsSetting?.joinBetaProgram;
        const address_country = payment_form_page_data?.confirmAddress?.data?.country;
        this.is_international_payment = address_country ? !this.case_insensitive_domestic_name_collection.includes(address_country.toUpperCase()) : false;
        // NOTES: Need to deep copy the value inside graphQL repsonse, as Vue will consider the original object as readOnly
        this.billingInfo = JSON.parse(JSON.stringify(payment_form_page_data?.invoice));
        this.charge_type = payment_form_page_data?.invoice?.charge_type;
        // NOTES: issue_type_id: 84 is the key reprenting billing issue type.
        // NOTES: issue_type_id: 104 is the key reprenting family order billing issue type.
        this.has_billing_issue = payment_form_page_data?.issueData?.issues?.some((issue)=>(issue.fk?.issue_type_id == 84 || issue.fk?.issue_type_id == 104) && !issue.fk?.resolution?.is_resolved);
        this.provider_name = payment_form_page_data?.providerClinic?.providerName;
        this.clinic_name = payment_form_page_data?.providerClinic?.clinicName;

        //definitely call after fetchBillingInfo(), as it needs to use billingInfo?.currency
        await this.fetchSupportedBnplMethods();
      }catch(err){
        console.log('initiatePaymentFormPage Err',err);
        throw new Error(err?.message ?? "Failed to initiate Payment Form Page.");
      }
    },
    async initiatePaymentSucceedPage(){
    // the /payment_succeed (Payment Succeed Page) will only be visited to call initiatePaymentSucceedPage() in mounted, if redirected from other pages, which means that all ids are stored already.
      try{
        const payment_succeed_page_data = await this.getPaymentSucceedPageData(this.accession_id, this.sample_id, this.order_id, this.patient_id, this.customer_id);
        this.current_basic_and_shipping_information = payment_succeed_page_data?.from_patientPNS?.confirmAddress?.data;
        this.is_concierge_paid = payment_succeed_page_data?.from_orderV2?.is_concierge_paid;
        this.address_needed = payment_succeed_page_data?.from_orderV2?.bloodKitDeliveryMethod == "shipToPatient" || payment_succeed_page_data?.from_orderV2?.nonBloodKitDeliveryMethod == "shipToPatient";
        this.blood_draw_needed = payment_succeed_page_data?.from_orderV2?.bloodKitDeliveryMethod != null;
        this.non_blood_draw_needed = payment_succeed_page_data?.from_orderV2?.nonBloodKitDeliveryMethod != null;
        if(this.non_blood_draw_needed){
          this.getSampleType();
        }
      }catch(err){
        console.log('initiatePaymentSucceedPage Err',err);
        throw new Error(err?.message ?? 'Failed to initiate Payment Succeed Page.');
      }
    },
    // graphQL request
    async getAllIdsAndOrderSourceViaSampleId(sample_id){
    // on PaymentMedia Page
      const GET_ALL_IDs_AND_ORDER_SOURCE_VIA_SAMPLE = gql `
        query getAllIdsAndOrderSourceViaSampleId( $sampleIdList:[String!] ){
          patientOrder(orderInput:{ sampleIdList: $sampleIdList }){
            orders{
              order{
                patientId
                orderIdStr
                customerId
                julienBarcode
                sampleId
                order_source
              }
            }
          }
        }
      `;
      try{
        const { data, errors } = await pns_graphal_client.query({
          query: GET_ALL_IDs_AND_ORDER_SOURCE_VIA_SAMPLE,
          variables: {
            sampleIdList: [String(sample_id)]
          },
          context:{
            headers:{
              Authorization: `Bearer ${ this.token ? this.token : localStorage.getItem('pnsToken')}`
            }
          }
        });
        //As graphql won't trhow an error automatically, we need to manually check the error.
        if(errors && errors.length > 0){
          throw new Error(errors.map(err => err.message).join(' | '));
        }
        return Promise.resolve(data?.patientOrder?.orders?.[0]?.order);
      }catch(err){
        console.log('getAllIdsAndOrderSourceViaSampleId Error: ', err);
        throw new Error(err?.message ?? 'Failed to get ids and order source.');
      }
    },
    async getPaymentMediaPageData(accession_id, sample_id, order_id, patient_id, customer_id){
      const GET_PAYMENT_MEDIA_PAGE_DATA = gql `
        query getPaymentMediaPageData( $accession_id: String!, $sample_id: String!, $order_id: String!, $patient_id: String!, $customer_id:String ){
          patientPNS( PNSInput: { accession_id: $accession_id, sample_id: $sample_id, order_id: $order_id, patient_id: $patient_id, customer_id:$customer_id }){
            transaction
          }
        }
      `;
      try{
        const { data, errors } = await pns_graphal_client.query(
          {
            query: GET_PAYMENT_MEDIA_PAGE_DATA,
            variables:{
              accession_id: String(accession_id),   
              sample_id: String(sample_id),
              order_id: String(order_id),
              patient_id: String(patient_id),
              customer_id: String(customer_id)
            },
            context:{
              headers:{
                Authorization: `Bearer ${this.token ? this.token : localStorage.getItem('pnsToken')}`
              }
            }
          }
        )
        //As graphql won't trhow an error automatically, we need to manually check the error.
        if(errors && errors.length > 0){
          throw new Error(errors.map(err => err.message).join(' | '));
        }
        return Promise.resolve(data?.patientPNS);
      }catch(err){
        console.log('getPaymentMediaPageData Error: ', err);
        throw new Error(err?.message ?? "Failed to get Payment Media Page data.");
      }
    },
    async getAllIdsAndOrderTypeDataViaSampleId(sample_id){
      const GET_ALL_IDs_AND_ORDER_TYPE_VIA_SAMPLE = gql `
        query getAllIdsAndOrderTypeDataViaSampleId( $sampleIdList:[String!] ){
          patientOrder(orderInput:{ sampleIdList: $sampleIdList }){
            orders{
              order{
                patientId
                orderIdStr
                customerId
                julienBarcode
                sampleId
                providerHasRevenue
                providerHasMerchandise
                chargeMethod
                bloodKitDeliveryMethod
                nonBloodKitDeliveryMethod
                orderItems {
                  item_id
                  item_type
                  x_choose_y_List {
                    x_choose_y_detail_list {
                        item_id
                        item_type
                    }
                  }
                }
                isFamilyOrder
                familyOrderSampleIds
              }
            }
          }
        }
      `;
      try{
        const { data, errors } = await pns_graphal_client.query({
          query: GET_ALL_IDs_AND_ORDER_TYPE_VIA_SAMPLE,
          variables: {
            sampleIdList: [String(sample_id)]
          },
          context:{
            headers:{
              Authorization: `Bearer ${ this.token ? this.token : localStorage.getItem('pnsToken')}`
            }
          }
        });
        //As graphql won't trhow an error automatically, we need to manually check the error.
        if(errors && errors.length > 0){
          throw new Error(errors.map(err => err.message).join(' | '));
        }
        return Promise.resolve(data?.patientOrder?.orders?.[0]?.order);
      }catch(err){
        console.log('getAllIdsAndOrderTypeDataViaSampleId Error: ', err);
        throw new Error(err?.message ?? 'Failed to get ids and order type related data.');
      }
    },
    async getPaymentFormPageData(accession_id, sample_id, order_id, patient_id, customer_id){
      const GET_PAYMENT_FORM_PAGE_DATA = gql `
        query getPaymentFormPageData( $accession_id: String!, $sample_id: String!, $order_id: String!, $patient_id: String!, $customer_id:String ){
          patientPNS(PNSInput: { accession_id: $accession_id, sample_id: $sample_id, order_id: $order_id, patient_id: $patient_id, customer_id:$customer_id }){
            pnsSetting {
              pns_brand_logo
              consultation_fee_name
              joinBetaProgram
            }
            issueData {
              issues {
                fk {
                  issue_type_id
                  resolution {
                    is_resolved
                  }
                }
              }
            }
            providerClinic {
                clinicName
                providerName
            }
            confirmAddress {
              data {
                country
              }
            }
            invoice {
              charge_id
              charge_type
              charge_type_id
              currency
              account_type
              account_id
              accession_id
              sample_id
              order_id
              is_paid
              total
              subtotal
              discount
              tax
              customer_id
              va_processing_fee
              ny_processing_fee
              drop_ship_fee
              international_shipping_fee
              provider_has_revenue
              consultation_fee
              applied_payments {
                apply_id
                payment_id
                transaction_id
                payment_type
                payment_source
                last_four
                amount
                service_date
              }
              patient_bill {
                charge_id
                charge_type
                charge_type_id
                currency
                account_type
                account_id
                subtotal
                is_paid
                total
                applied_payments {
                  apply_id
                  payment_id
                  transaction_id
                  payment_type
                  payment_source
                  last_four
                  amount
                  service_date
                }
              }
              family_orders {
                total
                patient_total
                family_order_details {
                  charge_id
                  charge_type
                  charge_type_id
                  currency
                  account_type
                  account_id
                  accession_id
                  sample_id
                  order_id
                  is_paid
                  total
                  subtotal
                  discount
                  tax
                  customer_id
                  va_processing_fee
                  international_shipping_fee
                  is_markup_order
                  provider_has_revenue
                  consultation_fee
                  applied_payments {
                    apply_id
                    payment_id
                    transaction_id
                    payment_type
                    payment_source
                    last_four
                    amount
                    service_date
                  }
                  patient_bill {
                    charge_id
                    charge_type
                    charge_type_id
                    currency
                    account_type
                    account_id
                    subtotal
                    is_paid
                    total
                    applied_payments {
                      apply_id
                      payment_id
                      transaction_id
                      payment_type
                      payment_source
                      last_four
                      amount
                      service_date
                    }
                  }
                  fees {
                    va_processing_fee
                    international_shipping_fee
                  }
                }
              }
            }
          }
        }
      `;
      try{
        const { data, errors } = await pns_graphal_client.query(
          {
            query: GET_PAYMENT_FORM_PAGE_DATA,
            variables:{
              accession_id: String(accession_id),   
              sample_id: String(sample_id),
              order_id: String(order_id),
              patient_id: String(patient_id),
              customer_id: String(customer_id)
            },
            context:{
              headers:{
                Authorization: `Bearer ${this.token ? this.token : localStorage.getItem('pnsToken')}`
              }
            },
            // NOTES: need to gaurantee that the data is the updated value via always fetching from the server, not from the cache.
            // NOTES: the information section and payment form might edit the data, so we need to always fetch the latest data from the server.
            fetchPolicy: 'network-only'
          }
        )
        //As graphql won't trhow an error automatically, we need to manually check the error.
        if(errors && errors.length > 0){
          throw new Error(errors.map(err => err.message).join(' | '));
        }
        return Promise.resolve(data?.patientPNS);
      }catch(err){
        console.log('getPaymentFormPageData error: ', err);
        throw new Error(err?.message ?? "Failed to get Payment Form Page data.");
      }
    },
    async getPaymentSucceedPageData(accession_id, sample_id, order_id, patient_id, customer_id){
      const GET_PAYMENT_SUCCEED_PAGE_DATA = gql `
        query getPaymentSucceedPageData( $accession_id: String!, $sample_id: String!, $order_id: String!, $patient_id: String!, $customer_id:String ){
          patientOrder (orderInput: {sampleIdList:[$sample_id]}) {
            orders {
              order {
                is_concierge_paid
                bloodKitDeliveryMethod
                nonBloodKitDeliveryMethod
              }
            }
          }
          patientPNS( PNSInput: { accession_id: $accession_id, sample_id: $sample_id, order_id: $order_id, patient_id: $patient_id, customer_id:$customer_id }){
            confirmAddress {
              data {
                name
                gender
                birthdate
                phone
                street
                apt_po
                city
                state
                zipcode
                country
                is_editable
                reason
              }
            }
          }
        }
      `;
      try{
        const { data, errors } = await pns_graphal_client.query(
          {
            query: GET_PAYMENT_SUCCEED_PAGE_DATA,
            variables:{
              accession_id: String(accession_id),   
              sample_id: String(sample_id),
              order_id: String(order_id),
              patient_id: String(patient_id),
              customer_id: String(customer_id)
            },
            context:{
              headers:{
                Authorization: `Bearer ${this.token ? this.token : localStorage.getItem('pnsToken')}`
              }
            },
            // NOTES: need to gaurantee that the data is the updated value via always fetching from the server, not from the cache.
            // NOTES: the information section and payment form might edit the data, so we need to always fetch the latest data from the server.
            fetchPolicy: 'network-only'
          }
        )
        //As graphql won't trhow an error automatically, we need to manually check the error.
        if(errors && errors.length > 0){
          throw new Error(errors.map(err => err.message).join(' | '));
        }
        return Promise.resolve({
          from_patientPNS: data?.patientPNS,
          from_orderV2: data?.patientOrder?.orders?.[0]?.order
        });
      }catch(err){
        console.log('getPaymentSucceedPageData error: ', err);
        throw new Error(err?.message ?? "Failed to get Payment Succeed Page data.");
      }
    },
    getSampleType(){
      orderBaseURL.get('/nonBloodSampleTubeType',{
        headers:{ 
          Authorization: `Bearer ${localStorage.getItem('pnsToken')?localStorage.getItem('pnsToken'):this.token}`
        },
        params:{
          sampleId:Number(this.sample_id),
        },
      }).then((res)=>{
        this.sample_types_params = this.convertSampleTypesToNextStepParams(res.data);
      }).catch((err)=>{
        console.log('getSampleType error', err);
        this.sample_types_params = ''
      })
    },
    convertSampleTypesToNextStepParams(backend_sample_types){
      //white spaces in each sample type was replaced by '+'
      //connect each sample type by '-'
      var output = '';
      for(const sample_type of backend_sample_types){
        const converted_sample_type = this.sample_types_mapping_backend_to_url_params[sample_type]
        ?
        this.sample_types_mapping_backend_to_url_params[sample_type].replaceAll(' ','+')
        :
        '';
        if(output){
          output += `-${converted_sample_type}`
        }else{
          output = converted_sample_type;
        }
      }
      return output;
    },
    async getBillingInfo() {
      const params = {
        charge_type_id: this.order_id,
        charge_type: this.charge_type,
      };
      try {
        const res = await billingService.get("/patientInvoice", { params: params });
        this.billingInfo = res.data;
        console.log("fetch billing info", res.data);
        localStorage.setItem("billingInfo", JSON.stringify(res.data));
        return Promise.resolve(res.data);
      } catch (err) {
        console.log("failed to retrieve billing info", err);
        this.billingInfo = "";
        localStorage.removeItem("billingInfo");
        return Promise.reject(err);
      }
    },
    async fetchSupportedBnplMethods(){
      try{
        const token = localStorage.getItem('pnsToken') ? localStorage.getItem('pnsToken') : this.token;
        const res = await payoutBaseURL.get('/recipient/getEligibilityOfBNPL', {
          params:{
            currency: this.billingInfo?.currency,
            order_id: this.order_id,
          },
          headers:{
            Authorization: `Bearer ${token}`
          }
        });
        this.supported_bnpl_methods_data = res.data;
        // eg. {
        //   klarna_eligible:true,
        //   afterpay_clearpay_eligible:true,
        //   //need to used for bnpl charging api
        //   connect_account_id:null,
        // };
        return Promise.resolve(res.data);
      }catch(err){
        console.log("fetchSupportedBNPLMethods error", err);
        this.supported_bnpl_methods_data=null;
        throw new Error('Failed to confirm whether this order supports Buy Now Pay Later methods');
      }
    },
    async applyPromo(promoCode) {
      const params = {
        coupon_code: promoCode,
        need_apply: true,
        charge_url: this.charge_type + "::" + this.order_id,
      };
      this.loadingPromo = true;
      try {
        const res = await billingService.post("/verifyCouponCode", params);
        this.loadingPromo = false;
        this.getBillingInfo();
        return Promise.resolve(res.data);
      } catch (err) {
        this.loadingPromo = false;
        return Promise.reject(err);
      }
    },
    async generateSplititIntent(installment_count) {
      // Call backend to get the checkout_url
      var charge_details = this.is_family_order
      ? this.billingInfo?.family_orders?.family_order_details?.map((family_order_detail_unit)=>{
        return {
          charge_type: family_order_detail_unit.charge_type,
          charge_type_id: family_order_detail_unit.charge_type_id.trim().replace(/^["](.*)["]$/, '$1'),
          sample_id: Number(family_order_detail_unit.sample_id),
          price: Number(family_order_detail_unit.patient_bill?.total ?? 0)
        }
      })
      : [{
        charge_type: this.charge_type,
        charge_type_id: this.order_id,
        sample_id: Number(this.sample_id),
        price: Number(this.billingInfo?.patient_bill?.total ?? 0)
      }];
      const params = {
        //if billingInfo OR patient_bill is null, use patient_id as account_id, since only patient will pay at PNS side
        account_id: this.billingInfo
          ? Number(this.billingInfo?.patient_bill?.account_id ?? this.patient_id)
          : null,
        account_type: 'patient',
        amount: (this.is_family_order ? Number(this.billingInfo?.family_orders?.patient_total ?? 0) : Number(this.billingInfo?.patient_bill?.total ?? 0)),
        currency: this.billingInfo?.currency,
        charge_details: charge_details,
        token_platform: 'splitit',
        num_of_installments: installment_count,
        type: "card",
      };
      try {
        const res = await chargeService.post(
          "/transaction/payOrders",
          params
        );
        return Promise.resolve(res.data);
      } catch (err) {
        console.log("failed to generate split it checkout url", err);
        return Promise.reject(err);
      }
    },
    async confirmSplitit(planNumber) {
      try {
        const res = await chargeService.post('/transaction/splitit/confirm', {
          plan_number: planNumber,
          without_apply: false,
        });
        return Promise.resolve(res.data);
      } catch (err) {
        console.log('Failed to confirm Splitit plan', err);
        return Promise.reject(err);
      }
    },
    async generateBnplIntent(provider) {
      var charge_details = this.is_family_order
      ? this.billingInfo?.family_orders?.family_order_details?.map((family_order_detail_unit)=>{
        return {
          charge_type: family_order_detail_unit.charge_type,
          charge_type_id: family_order_detail_unit.charge_type_id.trim().replace(/^["](.*)["]$/, '$1'),
          sample_id: Number(family_order_detail_unit.sample_id),
          price: Number(family_order_detail_unit.patient_bill?.total ?? 0)
        }
      })
      : [{
        charge_type: this.charge_type,
        charge_type_id: this.order_id,
        sample_id: Number(this.sample_id),
        price: Number(this.billingInfo?.patient_bill?.total ?? 0)
      }];
      const params = {
        //if billingInfo OR patient_bill is null, use patient_id as account_id, since only patient will pay at PNS side
        account_id: this.billingInfo
          ? Number(this.billingInfo?.patient_bill?.account_id ?? this.patient_id)
          : null,
        account_type: 'patient',
        amount: (this.is_family_order ? Number(this.billingInfo?.family_orders?.patient_total ?? 0) : Number(this.billingInfo?.patient_bill?.total ?? 0)),
        currency: this.billingInfo?.currency,
        charge_details: charge_details,
        bnpl_provider: provider,
        token_platform: this.provider_has_revenue == true ? "stripe_zb" : "stripe",
        connect_account_id: this.supported_bnpl_methods_data.connect_account_id,
        type: "bnpl",
      };
      try {
        const res = await chargeService.post("/transaction/payOrderWithBnpl", params);
        return Promise.resolve(res.data.client_secret);
      } catch (err) {
        console.log("failed to generate bnpl intent", err);
        return Promise.reject(err);
      }
    },
    async generateStripeCardPayIntent(isHSACard) {
      let amountToPay;
      if (this.amount_to_pay) {
        amountToPay = parseFloat(
          (Number(this.amount_to_pay) / 100).toFixed(2)
        );
      } else {
        if (this.billingInfo) {
          amountToPay = (this.is_family_order ? Number(this.billingInfo?.family_orders?.patient_total ?? 0) : Number(this.billingInfo?.patient_bill?.total ?? 0));
        } else {
          amountToPay = null;
        }
      }
      var charge_details = this.amount_to_pay
      ? [{
        charge_type: this.charge_type,
        charge_type_id: this.order_id,
        sample_id: Number(this.sample_id),
        price: amountToPay
      }]
      : (
        this.is_family_order
        ? this.billingInfo?.family_orders?.family_order_details?.map((family_order_detail_unit)=>{
          return {
            charge_type: family_order_detail_unit.charge_type,
            charge_type_id: family_order_detail_unit.charge_type_id.trim().replace(/^["](.*)["]$/, '$1'),
            sample_id: Number(family_order_detail_unit.sample_id),
            price: Number(family_order_detail_unit.patient_bill?.total ?? 0)
          }
        })
        : [{
          charge_type: this.charge_type,
          charge_type_id: this.order_id,
          sample_id: Number(this.sample_id),
          price: Number(this.billingInfo?.patient_bill?.total ?? 0)
        }]
      );
      const params = {
        //if billingInfo OR patient_bill is null, use patient_id as account_id, since only patient will pay at PNS side
        account_id: this.billingInfo
          ? Number(this.billingInfo?.patient_bill?.account_id ?? this.patient_id)
          : null,
        account_type: 'patient',
        amount: amountToPay,
        currency: this.billingInfo?.currency,
        charge_details: charge_details,
        // provider_has_revenue has highest priority. So if provider_has_revenue is true, token_platform will always be "stripe_zb" regardless of isHSACard.
        // And for HSA card || provider_has_revenue is false, we go to VA stripe directly.
        // And since HSA card will be hidden when provider_has_revenue is true, so no need to check isHSACard here.
        token_platform: this.provider_has_revenue == true ? "stripe_zb" : "stripe",
        type: "card",
      };
      if (isHSACard) {
        try {
          const res = await chargeService.post(
            "/transaction/payOrdersWithHFSA",
            params
          );
          return Promise.resolve(res.data.client_secret);
        } catch (err) {
          console.log("failed to generate stripe card pay intent", err);
          return Promise.reject(err);
        }
      } else {
        try {
          const res = await chargeService.post(
            "/transaction/payOrders",
            params
          );
          return Promise.resolve(res.data.client_secret);
        } catch (err) {
          console.log("failed to generate stripe card pay intent", err);
          return Promise.reject(err);
        }
      }
    },
    async generateStripeDigitalWalletPayIntent() {
      var charge_details = this.is_family_order
      ? this.billingInfo?.family_orders?.family_order_details?.map((family_order_detail_unit)=>{
        return {
          charge_type: family_order_detail_unit.charge_type,
          charge_type_id: family_order_detail_unit.charge_type_id.trim().replace(/^["](.*)["]$/, '$1'),
          sample_id: Number(family_order_detail_unit.sample_id),
          price: Number(family_order_detail_unit.patient_bill?.total ?? 0)
        }
      })
      : [{
        charge_type: this.charge_type,
        charge_type_id: this.order_id,
        sample_id: Number(this.sample_id),
        price: Number(this.billingInfo?.patient_bill?.total ?? 0)
      }];
      const params = {
        //if billingInfo OR patient_bill is null, use patient_id as account_id, since only patient will pay at PNS side
        account_id: this.billingInfo
          ? Number(this.billingInfo?.patient_bill?.account_id ?? this.patient_id)
          : null,
        account_type: 'patient',
        type: "card",
        amount: (this.is_family_order ? Number(this.billingInfo?.family_orders?.patient_total ?? 0) : Number(this.billingInfo?.patient_bill?.total ?? 0)),
        currency: this.billingInfo?.currency,
        charge_details: charge_details,
        token_platform: this.provider_has_revenue == true ? "stripe_zb" : "stripe",
      };
      console.log("generate stripe card pay intent", params);
      try {
        const res = await chargeService.post(
          "/transaction/payOrdersWithDigitalWallets",
          params
        );
        return Promise.resolve(res.data.client_secret);
      } catch (err) {
        console.log("failed to generate stripe digital pay intent", err);
        return Promise.reject(err);
      }
    },
    async getPaymentStatus() {
      try {
        const res = await chargeService.get(
          `/transaction/${this.order_id}/type/${this.charge_type}`
        );
        console.log("payment status", res.data);
        return Promise.resolve(res.data);
      } catch (err) {
        console.log("failed to get payment status", err);
        throw new Error(err?.message ?? "Failed to get payment status");
      }
    },
    async payByCard(token) {
      let amountToPay;
      if (this.amount_to_pay) {
        amountToPay = parseFloat(
          (Number(this.amount_to_pay) / 100).toFixed(2)
        );
      } else {
        if (this.billingInfo) {
          amountToPay = (this.is_family_order ? Number(this.billingInfo?.family_orders?.patient_total ?? 0) : Number(this.billingInfo?.patient_bill?.total ?? 0));
        } else {
          amountToPay = null;
        }
      }
      var charge_details = this.amount_to_pay
      ? [{
        charge_type: this.charge_type,
        charge_type_id: this.order_id,
        sample_id: Number(this.sample_id),
        price: amountToPay
      }]
      : (
        this.is_family_order
        ? this.billingInfo?.family_orders?.family_order_details?.map((family_order_detail_unit)=>{
          return {
            charge_type: family_order_detail_unit.charge_type,
            charge_type_id: family_order_detail_unit.charge_type_id.trim().replace(/^["](.*)["]$/, '$1'),
            sample_id: Number(family_order_detail_unit.sample_id),
            price: Number(family_order_detail_unit.patient_bill?.total ?? 0)
          }
        })
        : [{
          charge_type: this.charge_type,
          charge_type_id: this.order_id,
          sample_id: Number(this.sample_id),
          price: Number(this.billingInfo?.patient_bill?.total ?? 0)
        }]
      );
      const params = {
        //if billingInfo OR patient_bill is null, use patient_id as account_id, since only patient will pay at PNS side
        account_id: this.billingInfo
          ? Number(this.billingInfo?.patient_bill?.account_id ?? this.patient_id)
          : null,
        account_type: 'patient',
        amount: amountToPay,
        currency: this.billingInfo?.currency,
        type: "card",
        token_platform: "stax",
        payment_token: token,
        charge_details: charge_details,
      };
      console.log('params', params);
      try {
        // console.log('payByCard', localStorage.getItem('pnsToken'), this.token);
        //debug missing token
        if(localStorage.getItem('pnsToken')){
          const res = await chargeService.post("/transaction/payOrders", params);
          return Promise.resolve(res.data);
        }else{
          const res = await chargeService.post("/transaction/payOrders", params,
            {
              headers:{ 
                  Authorization: `Bearer ${this.token}`
                },
            }
          );
          return Promise.resolve(res.data);
        }
      } catch (err) {
        console.log("failed to pay by card", err);
        return Promise.reject(err);
      }
    },
    setCheckEmptyFields(val) {
      this.checkEmptyFields = val;
    },
    setInvalidCountryWarning(val) {
      this.invalidCountryWarning = val;
    },
    initHasEmptyFields() {
      this.hasEmptyFields = [];
    },
    updateHasEmptyFields(val) {
      this.hasEmptyFields.push(val);
    },
    setBillingEmail(val) {
      if (val) {
        this.billing_email = val;
      }
    },
    setBillingPhone(val) {
      this.billing_phone = val;
    },
    setBillingCountry(val) {
      this.billing_country = val;
    },
    setBillingState(val) {
      this.billing_state = val;
    },
    setBillingAddress(val) {
      this.billing_address = val;
    },
    setBillingAddress2(val) {
      this.billing_address2 = val;
    },
    setBillingCity(val) {
      this.billing_city = val;
    },
    setBillingZipCode(val) {
      this.billing_zipCode = val;
    },
    setBillingFirstName(val) {
      this.billing_firstName = val;
    },
    setBillingLastName(val) {
      this.billing_lastName = val;
    },
  },
});
